/**
 * Monitored User Selection Service
 *
 * For monitor-only users: tracks which monitored person is currently
 * selected for viewing across Dashboard, Today, and History pages.
 *
 * Data is synced into a local Dexie DB using the same manifest/pull
 * pipeline as the normal user, so date navigation is instant.
 * SSE subscriptions provide real-time updates.
 *
 * Selection persists across app restarts via localStorage.
 */

import {
  relationshipService,
  type MonitoredUser,
} from "./relationships";
import { authService, type UserRole } from "./auth";
import { syncService } from "./sync";
import { dbManager, type DafneUserDB } from "../db/user-database";
import { SseHub } from "./notifications/sse-hub";
import type { PullResult } from "./sync/types";

const STORAGE_KEY = "dafne_monitor_selected";

// Tables to sync and their date fields that need ISO→Date conversion
const SYNC_TABLES = [
  "entries",
  "periods",
  "settings",
  "cgmReadings",
  "dayNotes",
] as const;
type SyncTable = (typeof SYNC_TABLES)[number];

const DATE_FIELDS: Record<SyncTable, string[]> = {
  entries: ["timestamp", "createdAt", "updatedAt"],
  periods: [],
  settings: [],
  cgmReadings: ["timestamp"],
  dayNotes: [],
};

function sanitizeDates(
  table: SyncTable,
  data: Record<string, unknown>,
): Record<string, unknown> {
  const fields = DATE_FIELDS[table];
  if (fields.length === 0) return data;
  const out = { ...data };
  for (const f of fields) {
    const v = out[f];
    if (v && typeof v === "string") out[f] = new Date(v);
    else if (v && typeof v === "number") out[f] = new Date(v);
  }
  return out;
}

export interface SelectedMonitoredUser {
  userId: string;
  displayName: string;
  email?: string;
  relationshipType: "monitor" | "clinician";
}

type Listener = (selected: SelectedMonitoredUser | null) => void;

class MonitoredUserSelectionService {
  private _selected: SelectedMonitoredUser | null = null;
  private _listeners = new Set<Listener>();
  private _users: MonitoredUser[] = [];
  private _loaded = false;
  private _loadPromise: Promise<void> | null = null;
  private _db: DafneUserDB | null = null;
  private _dataReady = false;
  private _dataReadyPromise: Promise<void> | null = null;
  private _sseUnsub: (() => void) | null = null;
  private _refreshTimer: ReturnType<typeof setInterval> | null = null;

  get selected(): SelectedMonitoredUser | null {
    return this._selected;
  }

  get users(): MonitoredUser[] {
    return this._users;
  }

  get loaded(): boolean {
    return this._loaded;
  }

  /** True when the current user is a monitor or clinician without diabetic role. */
  get isMonitorOnly(): boolean {
    const roles: UserRole[] = authService.state.roles || [];
    return (
      (roles.includes("monitor") || roles.includes("clinician")) &&
      !roles.includes("diabetic")
    );
  }

  /** The local Dexie DB for the selected monitored person. */
  get db(): DafneUserDB | null {
    return this._db;
  }

  /** Whether local DB has been populated with data. */
  get dataReady(): boolean {
    return this._dataReady;
  }

  /** Resolves once local data is populated after selection. */
  whenDataReady(): Promise<void> {
    if (this._dataReady) return Promise.resolve();
    if (this._dataReadyPromise) return this._dataReadyPromise;
    return Promise.resolve();
  }

  subscribe(listener: Listener): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  private _notify() {
    for (const fn of this._listeners) fn(this._selected);
  }

  // ----------------------------------------------------------
  // Selection
  // ----------------------------------------------------------

  async select(user: SelectedMonitoredUser | null) {
    const prev = this._selected;
    this._selected = user;
    this._dataReady = false;

    // Tear down previous SSE / timer
    this._teardownLive();

    if (user) {
      // Persist
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
      } catch { /* quota */ }

      // Open local DB and sync
      this._dataReadyPromise = this._syncMonitoredDb(user.userId);
      await this._dataReadyPromise;

      // Subscribe to real-time events
      this._setupLive(user.userId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      this._db = null;
    }

    // Close previous DB if switching people
    if (prev && prev.userId !== user?.userId) {
      dbManager.closeMonitoredDatabase(prev.userId);
    }

    this._notify();
  }

  // ----------------------------------------------------------
  // Proper manifest → pull sync (same as normal user)
  // ----------------------------------------------------------

  private async _syncMonitoredDb(userId: string): Promise<void> {
    try {
      const monitoredDb = await dbManager.getMonitoredDatabase(userId);
      this._db = monitoredDb;

      // Get manifest to see what has changed
      const manifest = await syncService.getMonitoredManifest(userId);
      if (!manifest) {
        this._dataReady = true;
        return;
      }

      // Read local table versions from syncMetadata
      let localVersions: Record<string, number> = {};
      try {
        const meta = await monitoredDb.syncMetadata.get("state");
        if (meta && (meta as any).tableVersions) {
          localVersions = (meta as any).tableVersions;
        }
      } catch { /* first run, no metadata yet */ }

      // Pull each table where server version > local version
      for (const table of SYNC_TABLES) {
        const serverVer =
          manifest.tableVersions[table as keyof typeof manifest.tableVersions] ?? 0;
        const localVer = localVersions[table] ?? 0;

        if (serverVer <= localVer) continue; // up to date

        const result: PullResult | null = await syncService.pullMonitoredData(
          userId,
          table,
          localVer,
        );
        if (!result) continue;

        // Apply changes to local DB
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tableRef = (monitoredDb as any)[table];
        for (const change of result.changes) {
          if (change.isDeleted) {
            await tableRef.delete(change.id);
          } else {
            const data = sanitizeDates(
              table,
              change.data as Record<string, unknown>,
            );
            await tableRef.put({
              ...data,
              id: change.id,
              _syncStatus: "synced",
              _serverVersion: change.version,
            });
          }
        }

        // Track version
        localVersions[table] = result.currentVersion;
      }

      // Persist table versions
      await monitoredDb.syncMetadata.put({
        key: "state",
        tableVersions: localVersions,
        localVersion: manifest.etag,
        lastSyncTimestamp: new Date().toISOString(),
      });

      this._dataReady = true;
    } catch (err) {
      console.error("Failed to sync monitored user DB:", err);
      this._dataReady = true;
    }
  }

  /** Re-sync from server (called on SSE event or periodic timer). */
  async refresh(): Promise<void> {
    if (!this._selected || !this._db) return;
    await this._syncMonitoredDb(this._selected.userId);
    this._notify(); // tell pages data may have changed
  }

  // ----------------------------------------------------------
  // Real-time: SSE + periodic refresh
  // ----------------------------------------------------------

  private _setupLive(patientId: string) {
    // SSE subscription for patient data changes
    if (SseHub.isInitialized) {
      const hub = SseHub.instance;
      this._sseUnsub = hub.subscribePatient(patientId, () => {
        this.refresh();
      });
    }

    // Also do a periodic refresh every 5 minutes as fallback
    this._refreshTimer = setInterval(() => {
      this.refresh();
    }, 5 * 60 * 1000);
  }

  private _teardownLive() {
    this._sseUnsub?.();
    this._sseUnsub = null;
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  // ----------------------------------------------------------
  // Initial load (fetch monitored users list)
  // ----------------------------------------------------------

  load(): Promise<void> {
    if (this._loaded) return Promise.resolve();
    if (this._loadPromise) return this._loadPromise;
    this._loadPromise = this._doLoad();
    return this._loadPromise;
  }

  whenLoaded(): Promise<void> {
    if (this._loaded) return Promise.resolve();
    if (this._loadPromise) return this._loadPromise;
    return new Promise<void>((resolve) => {
      const unsub = this.subscribe(() => {
        if (this._loaded) {
          unsub();
          resolve();
        }
      });
      if (this._loaded) {
        unsub();
        resolve();
      }
    });
  }

  private async _doLoad(): Promise<void> {
    try {
      this._users = await relationshipService.getMonitoredUsers();
      this._loaded = true;

      // Restore persisted selection (or auto-select if only one)
      const restored = this._restoreSelection();
      if (restored) {
        await this.select(restored);
      } else if (this._users.length === 1 && !this._selected) {
        const u = this._users[0];
        await this.select({
          userId: u.userId,
          displayName: u.displayName,
          email: u.email,
          relationshipType: u.relationshipType,
        });
      } else {
        this._notify();
      }
    } catch (err) {
      console.error("Failed to load monitored users:", err);
      this._loaded = true;
      this._notify();
    }
  }

  /** Restore selection from localStorage if still valid. */
  private _restoreSelection(): SelectedMonitoredUser | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const saved: SelectedMonitoredUser = JSON.parse(raw);
      // Verify the user is still in the monitored list
      if (this._users.some((u) => u.userId === saved.userId)) {
        return saved;
      }
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* corrupt data */ }
    return null;
  }

  // ----------------------------------------------------------
  // Cleanup
  // ----------------------------------------------------------

  reset() {
    this._teardownLive();
    if (this._selected) {
      dbManager.closeMonitoredDatabase(this._selected.userId);
    }
    this._selected = null;
    this._users = [];
    this._loaded = false;
    this._loadPromise = null;
    this._db = null;
    this._dataReady = false;
    this._dataReadyPromise = null;
    localStorage.removeItem(STORAGE_KEY);
    this._notify();
  }
}

export const monitoredUserSelectionService =
  new MonitoredUserSelectionService();
